import { X } from 'lucide-react'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useT } from '../../i18n/t'
import { CONTEST_NEWS_PATH, CONTEST_POSTER_SRC } from '../../lib/contest'

type Props = {
  open: boolean
  onDismiss: () => void
  /** When true, show CTA to contest tab (site popup). Lightbox on contest page can omit it. */
  showCta?: boolean
}

export function ContestPosterModal({ open, onDismiss, showCta = true }: Props) {
  const { t } = useT()

  useEffect(() => {
    if (!open) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onDismiss])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[1260] flex items-end justify-center p-3 sm:items-center sm:p-6"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-ink/55"
        aria-label={t('contest.popup.closeAria')}
        onClick={onDismiss}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="contest-poster-modal-title"
        className="relative flex max-h-[88dvh] w-full max-w-[min(100%,420px)] flex-col bg-paper sm:max-w-[440px] md:max-w-[480px]"
      >
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-2 top-2 z-10 grid h-10 w-10 place-items-center text-ink/70 transition hover:bg-ink/[0.06] hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
          aria-label={t('contest.popup.closeAria')}
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
        <h2 id="contest-poster-modal-title" className="sr-only">
          {t('contest.popup.title')}
        </h2>
        <div className="min-h-0 flex-1 overflow-hidden">
          <img
            src={CONTEST_POSTER_SRC}
            alt={t('contest.poster.alt')}
            className="mx-auto block h-auto max-h-[min(72dvh,640px)] w-full object-contain"
            decoding="async"
          />
        </div>
        {showCta ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-ink/10 p-4">
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex h-11 items-center justify-center px-5 text-sm font-extrabold text-ink/70 transition hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
            >
              {t('contest.popup.later')}
            </button>
            <Link
              to={CONTEST_NEWS_PATH}
              onClick={onDismiss}
              className="inline-flex h-11 items-center justify-center bg-brand px-5 text-sm font-extrabold text-brand-ink transition hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
            >
              {t('contest.popup.cta')}
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  )
}
